import React, { FC, useEffect, useState } from 'react';

import useLocales from 'hooks/useLocales';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import IncomingRequests from 'components/shared/IncomingRequests/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';
import Navbar from 'components/Navbar';
import MatchResultHeader from 'components/matchResultHeader';
import { useParams } from 'react-router';
import Footer from 'components/Footer/Index';
import NoData from 'components/shared/NoData/Index';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import { CircularProgress, TextField } from '@mui/material';
import Pagination from '@material-ui/core/Pagination';
import { useDebounce } from 'helpers/useDebounce';

import './Index.scss';

const MatchCandidate: FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { t } = useLocales();
  const { matchOverViewOne, matchProfileOne } = useSelector(
    (state: RootState) => state.match
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [applicationCounts, setApplicationCounts] = useState<{
    incomingCount: number;
    hiredCount: number;
  }>({
    incomingCount: 0,
    hiredCount: 0
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const handleModal = (open: boolean, id: number) => {
    setOpenModal(open);

    // Find the candidate by ID
    const element = matchOverViewOne?.results?.find(
      (el: { id: number }) => el.id === id
    );

    if (!element) {
      return;
    }

    // Set the selected request
    setSelectedRequest(element);
  };

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          await MatchProfileService.getCandidatesOverview(
            +id,
            limit,
            offset,
            debouncedSearchQuery
          );
          await MatchProfileService.getMatchProfileById(+id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [id, limit, offset, debouncedSearchQuery]);

  useEffect(() => {
    if (matchOverViewOne?.results) {
      const newApplications = matchOverViewOne?.results?.filter(
        (el: { stage: number }) => el.stage === 0
      ).length;
      const hired = matchOverViewOne?.results?.filter(
        (el: { stage: number }) => el.stage === 5
      ).length;
      setApplicationCounts({
        incomingCount: newApplications,
        hiredCount: hired
      });
    }
  }, [matchOverViewOne]);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    if (matchOverViewOne && matchOverViewOne.count !== undefined) {
      setTotalCount(matchOverViewOne.count);
    }
  }, [matchOverViewOne]);

  useEffect(() => {
    setPage(1);
  }, [matchOverViewOne]);

  const cancelHandler = (id: number) => {
    MatchProfileService.deleteCultureProfile(id);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  return (
    <div
      className="matchCandidate"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <Navbar />
      <MatchResultHeader data={matchProfileOne} />
      <div className="main" style={{ flex: 1 }}>
        <div className="candidateOver">
          <div className="candidateOver__right">
            <div className="candidateOver__right__top">
              <div className="candidateOver__right__top__counts">
                <button className="candidateOver__right__top__counts-warn btn">
                  {t('New applicant ')}
                  {`(${applicationCounts.incomingCount})`}
                </button>
                <button className="candidateOver__right__top__counts-pass btn">
                  {t('Candidate Hired ')}
                  {`(${applicationCounts.hiredCount})`}
                </button>
              </div>
              <div className="search-container">
                <TextField
                  value={searchQuery}
                  onChange={handleSearch}
                  className="dropdown-search"
                  placeholder="Search"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="candidateOver__right__bottom">
              {matchOverViewOne === null ? (
                <div className="loadingContainer">
                  <CircularProgress className="loading" />
                </div>
              ) : matchOverViewOne?.results?.length ? (
                matchOverViewOne?.results?.map((item: any) => (
                  <IncomingRequests
                    key={item.id}
                    id={item.id}
                    name={item.candidate.name}
                    email={item.candidate.email}
                    sub_title={item.candidate.pitch}
                    icon={item.candidate.avatar}
                    score={item.score}
                    culture_score={item.culture_score}
                    date={item.created_at}
                    hired_at={item.offered_at}
                    industry={item.candidate.industries}
                    stage={item.stage}
                    status={item.status}
                    // setStage={changeStage}
                    cancel={cancelHandler}
                    setOpen={() => handleModal(true, item.id)}
                  />
                ))
              ) : (
                <div className="no_data">
                  <NoData />
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil((matchOverViewOne?.count || 0) / limit)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>
        </div>
        {openModal && selectedRequest && (
          <Popup
            className="popUp"
            title={t('Match Profile')}
            description={matchProfileOne?.title}
            onClose={handleClosePopup}
          >
            <ProfilePopUp
              selectedRequest={selectedRequest}
              getJob={matchProfileOne}
              id={selectedRequest?.id}
              onClick={() => cancelHandler(selectedRequest?.id)}
              status={selectedRequest.status}
              isCandidate={false}
            />
          </Popup>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MatchCandidate;
